import {
  useCreateCompanyContact,
  useCreateCompanyPhone,
  useCreateLocation,
  useListItems,
  useUpdateCompanyContact,
  useUpdateCompanyLocation,
  useUpdateCompanyPhone,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import {
  CreateCompanyContactBody,
  CreateLocationBody,
  GetCompanyContactByKey,
  GetCompanyPhoneByKey,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket/types';
import { useToastContext } from '@trufla-technology/trumarket-react-design-system';
import {
  OptionsType,
  ValueType,
} from '@trufla-technology/trumarket-react-design-system/lib/components/controls/MultiSelect';
import { formatAddress } from '@trufla-technology/trumarket-react-design-system/lib/utils/formatAddress';
import {
  Address,
  BranchFormType,
  LatLng,
  LocationResult,
  ResultObject,
  WorkingHours,
} from 'app/types/Branch';
import { IErrorResponse } from 'app/types/ErrorResponse';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

export const useBranchForm = (
  values: () => Partial<BranchFormType>,
  update: (values: Partial<BranchFormType>) => void,
  setLatLng: React.Dispatch<React.SetStateAction<LatLng>>,
  setAddress: React.Dispatch<React.SetStateAction<string>>,
  latLng: LatLng,
  branchKey: string | undefined,
  mode: string | undefined,
  phonesData: GetCompanyPhoneByKey[] | undefined,
  contactsData: GetCompanyContactByKey[] | undefined,
  setAddressValidation: React.Dispatch<React.SetStateAction<boolean>>,
  setPrimaryContactValidation: React.Dispatch<React.SetStateAction<boolean>>,
  form: any,
) => {
  const branchCodesGroupKey = '02bba357-91fb-4d2a-b756-eb26fd3bca1d';
  const { data: branchCodesData } = useListItems({
    filter: { group: branchCodesGroupKey },
  });
  const [branchCodes, setBranchCodes] = useState<
    OptionsType | null | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const CreateCompanyContact = useCreateCompanyContact();
  const CreateCompanyPhone = useCreateCompanyPhone();
  const UpdateCompanyLocation = useUpdateCompanyLocation();
  const UpdateCompanyContact = useUpdateCompanyContact();
  const UpdateCompanyPhone = useUpdateCompanyPhone();
  const CreateLocation = useCreateLocation();
  const history = useHistory();
  const toaster = useToastContext();

  useEffect(() => {
    const processBranchCodesData = () => {
      const branchCodesArray = branchCodesData?.[0]?.value as
        | string[]
        | undefined;
      const mappedBranchCodes = branchCodesArray?.map((item) => {
        if (!item) {
          return null;
        }
        return {
          label: item?.toUpperCase(),
          value: item,
        } as OptionsType[number];
      });
      return mappedBranchCodes?.filter(Boolean) as OptionsType | undefined;
    };

    const processedData = processBranchCodesData();
    if (processedData) {
      setBranchCodes(processedData);
    }
  }, [branchCodesData]);

  const getGeoLocation = (address: any) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: address }, function (results, status) {
      if (status === google.maps.GeocoderStatus.OK) {
        if (results?.length) {
          setLatLng({
            lat: results[0]?.geometry?.location?.lat(),
            lng: results[0]?.geometry?.location?.lng(),
          });
        }
      }
    });
  };
  const handleSelectAddress = (resultValues: ResultObject) => {
    getGeoLocation(resultValues.resultsAddress);
    const formValues = values();
    update({
      ...formValues,
      'address.fullAddress': resultValues.resultsAddress || '',
      'address.street': resultValues.streetLongName,
      'address.country': resultValues.countryShortName,
      'address.postalCode': resultValues.postalCodeLongName,
      'address.city':
        resultValues.countryShortName === 'US'
          ? resultValues.stateLongName
          : resultValues.cityShortName,
    });
  };

  const getAddress = (latLng: any) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, function (results, status) {
      if (status === google.maps.GeocoderStatus.OK) {
        if (results?.length) {
          handleSelectAddress(formatAddress({ results: results }));
          setAddress(formatAddress({ results: results })?.resultsAddress || '');
        }
      }
    });
  };

  const socialMediaValue = (
    socialMedia: string | undefined,
  ): string | undefined => {
    return socialMedia?.trim().length === 0 ? undefined : socialMedia;
  };
  const handleWebsiteRegex = (url: string) => {
    const regex = /^(?!https?:\/\/)(?!www\.)/;
    if (regex.test(url)) {
      url = 'https://www.' + url;
    }
    if (url.startsWith('www.')) url = 'https://' + url;

    return url;
  };

  const isEmpty = (objectToCheck: object) => {
    return (
      Object.values(objectToCheck)
        .flatMap((value) => {
          if (Array.isArray(value)) {
            return value.map((subValue) => Object.values(subValue));
          }
          return [value];
        })
        .flat()
        .filter((val) => val !== '' && val !== undefined).length > 0
    );
  };

  const excludeProperties = (
    object: object,
    propertiesToExclude: string[],
  ): object =>
    Object.fromEntries(
      Object.entries(object).filter(
        ([key]) => !propertiesToExclude.includes(key),
      ),
    );

  const validationChecker = (
    objectToCheck: object,
    setValidation: React.Dispatch<React.SetStateAction<any>>,
    form: any,
    exceptions?: string[],
  ) => {
    const hasNonEmptyValue = isEmpty(objectToCheck);

    let allValuesFilled = false;
    if (hasNonEmptyValue) {
      setValidation(true);
      form.form.validateFields();
      const filteredObject = excludeProperties(objectToCheck, exceptions ?? []);

      allValuesFilled = Object.values(
        exceptions ? filteredObject : objectToCheck,
      ).every((value) => value !== '' && value !== undefined);
    } else {
      setValidation(false);
    }

    return { allValuesFilled, hasNonEmptyValue };
  };
  const handleFinish = async (formData: Partial<BranchFormType>) => {
    const socialMediaData =
      formData['socialMedia.facebook'] ||
      formData['socialMedia.linkedIn'] ||
      formData['socialMedia.twitter'] ||
      formData['socialMedia.youtube'];
    const workingHours = [
      ...formData
        ?.workingHours!.filter(
          (workHour) => workHour.checkDay && workHour?.checkDay === true,
        )
        .map((workHour: BranchFormType['workingHours'][number]) => {
          return {
            from: workHour.from,
            to: workHour.to,
            day: workHour.day,
          };
        }),
    ];
    const useGlobalPrimaryContact = values().useGlobalPrimaryContact;
    const address: Address = {
      street: formData['address.street'],
      country: formData['address.country'],
      ...(formData['address.unitNumber']
        ? { unitNumber: formData['address.unitNumber'] }
        : {}),
      city: formData['address.city'],
      postalCode: formData['address.postalCode'],
      ...(formData['address.province'] && formData['address.province'] !== ''
        ? { province: formData['address.province'] }
        : {}),
      ...(formData['address.fullAddress'] &&
      formData['address.fullAddress'] !== ''
        ? { fullAddress: formData['address.fullAddress'] }
        : {}),
      ...(formData['address.floor'] && formData['address.floor'] !== ''
        ? { floor: formData['address.floor'] }
        : {}),
      ...(formData['address.suite'] && formData['address.suite'] !== ''
        ? { suite: formData['address.suite'] }
        : {}),
    };

    const primaryContact = {
      email: formData['primaryContact.email'],
      name: formData['primaryContact.name'],
      phones: formData['primaryContact.phones'],
      title: formData['primaryContact.title'],
    };

    const { hasNonEmptyValue, allValuesFilled } = validationChecker(
      address,
      setAddressValidation,
      form,
      ['province', 'fullAddress', 'floor', 'suite', 'unitNumber'],
    );
    const {
      hasNonEmptyValue: primaryContactNonEmpty,
      allValuesFilled: primaryContactFilled,
    } = validationChecker(primaryContact, setPrimaryContactValidation, form);

    if (
      (hasNonEmptyValue && !allValuesFilled) ||
      (primaryContactNonEmpty && !primaryContactFilled)
    ) {
      return;
    }

    const locationCodes = !!(formData?.codes as ValueType)?.[0].value
      ? (formData?.codes as ValueType)?.map((code) => code.value)
      : formData?.codes;

    const location = {
      name: formData.name!!,
      ...(formData?.website
        ? { website: handleWebsiteRegex(formData?.website) }
        : {}),
      headquarter: formData.headquarter,
      companySocialMedia: formData.useGlobalSocialMedia,
      ...(formData?.mailboxes?.[0].mail !== ''
        ? { mailboxes: formData.mailboxes }
        : {}),
      ...(socialMediaData && !formData.useGlobalSocialMedia
        ? {
            socialMedia: {
              facebook: socialMediaValue(formData['socialMedia.facebook']),
              twitter: socialMediaValue(formData['socialMedia.twitter']),
              linkedIn: socialMediaValue(formData['socialMedia.linkedIn']),
              youtube: socialMediaValue(formData['socialMedia.youtube']),
            },
          }
        : {}),
      ...(workingHours.length
        ? { workingHours: workingHours as WorkingHours }
        : {}),
      address: address.country ? address : undefined,
      ...(isNaN(latLng?.lng!) || !latLng?.lng
        ? {}
        : { longitude: latLng?.lng }),
      ...(isNaN(latLng?.lat!) || !latLng?.lat ? {} : { latitude: latLng?.lat }),
      ...(formData.contractNumbers?.length
        ? {
            contractNumbers: formData.contractNumbers.map((number) =>
              number.toString(),
            ),
          }
        : {}),
      ...((formData.codes as string[])?.length
        ? {
            codes: locationCodes,
          }
        : {}),
    };

    try {
      setLoading(true);

      let result: LocationResult;

      for (let index = 0; index < formData?.phones?.length!; index++) {
        const phone = formData?.phones?.[index];

        if (phonesData && phonesData.length > 0 && phonesData[index]?.key) {
          await UpdateCompanyPhone(phonesData[index].key!, {
            number: `${phone?.number}`,
            //@ts-ignore - until add more types in BE
            type: phone.type,
          });
        } else if (
          formData.phones &&
          formData.phones.length > 0 &&
          formData?.phones?.[index].number !== '' &&
          mode === 'edit'
        ) {
          await CreateCompanyPhone({
            number: `${phone?.number}`,
            //@ts-ignore - until add more types in BE
            type: phone.type,
            locationKey: branchKey,
          });
        }
      }
      if (mode !== 'edit') {
        let allApisSuccess = true;
        result = await CreateLocation(location as CreateLocationBody);
        setLoading(false);

        try {
          if (formData.phones?.length && formData.phones?.[0].number !== '') {
            await Promise.all(
              formData.phones.map(async (phone, index) => {
                try {
                  await CreateCompanyPhone({
                    number: `${formData.phones!![index].number}`,
                    //@ts-ignore - until add more types in BE
                    type: formData.phones[index].type,
                    locationKey: result.data?.key,
                  });
                } catch (error) {
                  allApisSuccess = false;
                }
              }),
            );
          }
          if (!useGlobalPrimaryContact) {
            (formData['primaryContact.name'] &&
              !formData['primaryContact.phones']?.some(
                (phone) => phone.number === '',
              )) ||
              (formData['primaryContact.email'] &&
                (await CreateCompanyContact({
                  name: formData['primaryContact.name'],
                  ...(formData['primaryContact.email']
                    ? { email: formData['primaryContact.email'] }
                    : {}),
                  ...(formData['primaryContact.title']
                    ? { title: formData['primaryContact.title'] }
                    : {}),
                  ...(!formData['primaryContact.phones']?.some(
                    (phone) => phone.number === '',
                  )
                    ? {
                        phones: [
                          ...formData['primaryContact.phones']!.map(
                            (contact) => {
                              return {
                                ...contact,
                                number: `${contact.number}`,
                              };
                            },
                          ),
                        ],
                      }
                    : {}),
                  ...(result.data?.key
                    ? { locationKey: result.data?.key }
                    : {}),
                } as CreateCompanyContactBody)));
          }
          if (allApisSuccess) {
            history.push('/configurations/company-details/');
            toaster.success('Successfully added!');
          } else {
            toaster.error('Failed to add phone');
            history.push(
              `/configurations/company-details/edit/${result?.data?.key}`,
            );
          }
        } catch (error) {
          setLoading(false);
          const errorMessage = (
            error as IErrorResponse
          )?.response?.data?.error.replaceAll('body.', '');
          toaster.error(errorMessage || 'Failed to add a branch');
          history.push(
            `/configurations/company-details/edit/${result?.data?.key}`,
          );
        }
      } else if (branchKey && mode === 'edit') {
        await UpdateCompanyLocation(branchKey, {
          ...location,
          workingHours: workingHours,
        } as CreateLocationBody);
        setLoading(false);

        if (!useGlobalPrimaryContact) {
          if (contactsData && contactsData[0] && contactsData[0].key) {
            await UpdateCompanyContact(contactsData[0].key, {
              name: formData['primaryContact.name'],
              email: formData['primaryContact.email'],
              title: formData['primaryContact.title'],
              //@ts-ignore - until add more types in BE
              phones: [
                ...formData['primaryContact.phones']!!.map((phone) => {
                  return { ...phone, number: `${phone.number}` };
                }),
              ],
              locationKey: branchKey,
            });
          } else {
            await CreateCompanyContact({
              name: formData['primaryContact.name']!!,
              email: formData['primaryContact.email'],
              title: formData['primaryContact.title'],
              //@ts-ignore - until add more types in BE
              phones: [
                ...formData['primaryContact.phones']!!.map((phone) => {
                  return { ...phone, number: `${phone.number}` };
                }),
              ],
              locationKey: branchKey,
            });
          }
        }

        toaster.success('Successfully changed!');
        history.push('/configurations/company-details/');
      }
    } catch (error) {
      setLoading(false);
      const errorMessage = (
        error as IErrorResponse
      )?.response?.data?.error.replaceAll('body.', '');
      toaster.error(
        errorMessage && mode === 'edit'
          ? `Failed to edit the branch: ${errorMessage}`
          : `Failed to add a branch: ${errorMessage}`,
      );
    }
  };

  return {
    getAddress,
    handleSelectAddress,
    handleFinish,
    history,
    toaster,
    branchCodes,
    loading,
  };
};
